import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import * as api from '../services/api';
import './NotificationDropdown.css';

const BellIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M12 3.25a5 5 0 00-5 5v1.7c0 .7-.2 1.38-.58 1.96L5.2 14.3c-.55.83.05 1.95 1.04 1.95h11.52c.99 0 1.59-1.12 1.04-1.95l-1.22-1.39A3.6 3.6 0 0117 9.95V8.25a5 5 0 00-5-5z"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinejoin="round"
    />
    <path
      d="M9.7 18.4a2.4 2.4 0 004.6 0"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
    />
  </svg>
);

const TYPE_META = {
  info: { label: 'i', tone: 'info' },
  success: { label: '✓', tone: 'success' },
  warning: { label: '!', tone: 'warning' },
  error: { label: '×', tone: 'error' },
  connection: { label: '◎', tone: 'connection' },
  system: { label: '⚙', tone: 'system' },
  ticket_created: { label: 'T', tone: 'info' },
  ticket_reply: { label: 'T', tone: 'success' },
  ticket_status: { label: 'T', tone: 'warning' },
  vpn_failover: { label: '⇄', tone: 'warning' },
};

const NotificationDropdown = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const dropdownRef = useRef(null);
  const hasLoaded = useRef(false);

  const applyPayload = (notifData) => {
    setNotifications(notifData?.notifications || []);
    if (typeof notifData?.unread_count === 'number') {
      setUnreadCount(notifData.unread_count);
    }
  };

  const loadNotifications = useCallback(async ({ silent = false } = {}) => {
    if (!silent && !hasLoaded.current) setLoading(true);
    try {
      const notifData = await api.getNotifications({ limit: 8 });
      applyPayload(notifData);
      hasLoaded.current = true;

      // If a failover happened, refresh active tunnel config for clients/listeners
      const failover = (notifData?.notifications || []).find(
        (n) => n.type === 'vpn_failover' && !n.is_read
      );
      if (failover) {
        try {
          const cfg = await api.getActiveVPNConfig();
          if (cfg?.success && cfg.config) {
            sessionStorage.setItem('arcvpn_failover_config', JSON.stringify(cfg));
            window.dispatchEvent(new CustomEvent('arcvpn:failover', { detail: cfg }));
          }
        } catch (e) {
          console.warn('Failover config refresh failed:', e);
        }
      }
    } catch (error) {
      console.error('Failed to load notifications:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadNotifications();
    const interval = setInterval(() => loadNotifications({ silent: true }), 30000);
    return () => clearInterval(interval);
  }, [loadNotifications]);

  useEffect(() => {
    if (isOpen) loadNotifications({ silent: true });
  }, [isOpen, loadNotifications]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleMarkAsRead = async (notificationId) => {
    const target = notifications.find((n) => n.id === notificationId);
    if (!target || target.is_read) return;

    setNotifications((prev) =>
      prev.map((n) => (n.id === notificationId ? { ...n, is_read: true } : n))
    );
    setUnreadCount((prev) => Math.max(0, prev - 1));

    try {
      await api.markNotificationAsRead(notificationId);
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
      loadNotifications({ silent: true });
    }
  };

  const handleMarkAllAsRead = async () => {
    const previous = notifications;
    const previousUnread = unreadCount;
    setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
    setUnreadCount(0);
    try {
      await api.markAllNotificationsAsRead();
    } catch (error) {
      console.error('Failed to mark all as read:', error);
      setNotifications(previous);
      setUnreadCount(previousUnread);
    }
  };

  const handleDelete = async (notificationId, event) => {
    event.stopPropagation();
    const target = notifications.find((n) => n.id === notificationId);
    if (!target || busyId === notificationId) return;

    const previous = notifications;
    const previousUnread = unreadCount;
    setBusyId(notificationId);
    setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
    if (!target.is_read) setUnreadCount((prev) => Math.max(0, prev - 1));

    try {
      const result = await api.deleteNotification(notificationId);
      if (typeof result?.unread_count === 'number') {
        setUnreadCount(result.unread_count);
      }
    } catch (error) {
      console.error('Failed to delete notification:', error);
      setNotifications(previous);
      setUnreadCount(previousUnread);
    } finally {
      setBusyId(null);
    }
  };

  const handleItemClick = async (notification) => {
    if (!notification.is_read) {
      await handleMarkAsRead(notification.id);
    }
    if (notification.link) {
      setIsOpen(false);
      navigate(notification.link);
    }
  };

  const openAll = () => {
    setIsOpen(false);
    navigate('/dashboard/notifications');
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return t('notifications.justNow', 'Just now');
    if (diffMins < 60) return t('notifications.minsAgo', '{{count}}m ago', { count: diffMins });
    if (diffHours < 24) return t('notifications.hoursAgo', '{{count}}h ago', { count: diffHours });
    if (diffDays < 7) return t('notifications.daysAgo', '{{count}}d ago', { count: diffDays });
    return date.toLocaleDateString();
  };

  const typeMeta = (type) => TYPE_META[type] || TYPE_META.info;

  return (
    <div className="notification-dropdown" ref={dropdownRef}>
      <button
        type="button"
        className={`notification-btn ${isOpen ? 'is-open' : ''}`}
        title={t('notifications.title', 'Notifications')}
        aria-label={t('notifications.title', 'Notifications')}
        aria-expanded={isOpen}
        onClick={() => setIsOpen((open) => !open)}
      >
        <BellIcon />
        {unreadCount > 0 && (
          <span className="notification-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
        )}
      </button>

      {isOpen && (
        <div className="notification-panel">
          <div className="notification-header">
            <div>
              <h3>{t('notifications.title', 'Notifications')}</h3>
              <p>
                {unreadCount > 0
                  ? t('notifications.unread', '{{count}} unread', { count: unreadCount })
                  : t('notifications.allCaughtUp', 'You’re all caught up')}
              </p>
            </div>
            {unreadCount > 0 && (
              <button type="button" className="mark-all-read-btn" onClick={handleMarkAllAsRead}>
                {t('notifications.markAll', 'Mark all read')}
              </button>
            )}
          </div>

          <div className="notification-list">
            {loading && !hasLoaded.current ? (
              <div className="notification-loading">
                <div className="spinner" />
                <p>{t('common.loading', 'Loading…')}</p>
              </div>
            ) : notifications.length === 0 ? (
              <div className="notification-empty">
                <div className="empty-mark">
                  <BellIcon />
                </div>
                <p>{t('notifications.empty', 'No notifications yet')}</p>
              </div>
            ) : (
              notifications.map((notification) => {
                const meta = typeMeta(notification.type);
                return (
                  <div
                    key={notification.id}
                    className={`notification-item ${!notification.is_read ? 'unread' : ''} ${
                      busyId === notification.id ? 'is-busy' : ''
                    }`}
                    onClick={() => handleItemClick(notification)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') handleItemClick(notification);
                    }}
                  >
                    <div className={`notification-icon tone-${meta.tone}`}>{meta.label}</div>
                    <div className="notification-content">
                      <div className="notification-title">{notification.title}</div>
                      <div className="notification-message">{notification.message}</div>
                      <div className="notification-time">{formatTime(notification.created_at)}</div>
                    </div>
                    <button
                      type="button"
                      className="notification-delete"
                      onClick={(e) => handleDelete(notification.id, e)}
                      title={t('notifications.delete', 'Delete')}
                      aria-label={t('notifications.delete', 'Delete')}
                    >
                      ×
                    </button>
                  </div>
                );
              })
            )}
          </div>

          <div className="notification-footer">
            <button type="button" className="view-all-btn" onClick={openAll}>
              {t('notifications.viewAll', 'View all notifications')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationDropdown;
